import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { format, isValid, parse } from "date-fns";
import Calendar from "../Calendar/Calendar";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import { Input } from "../Modals/DocumentModal/DocumentModal.styles";
import TitleTooltip from "../TitleTooltip/TitleTooltip";
import { CalendarInputRoot, CalendarPopup } from "./CalendarInput.styles";

interface DatePickerInputProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  title?: string;
}

interface PopupPosition {
  top: number;
  left: number;
}

const VIEWPORT_MARGIN = 16;
const POPUP_GAP = 8;

export const parseCalendarInputDate = (value?: string | null): Date | undefined => {
  const normalizedValue = (value ?? "").trim();
  if (!normalizedValue) return undefined;

  const parsedDate = parse(normalizedValue, "dd.MM.yyyy", new Date());
  return isValid(parsedDate) ? parsedDate : undefined;
};

const DatePickerInput: React.FC<DatePickerInputProps> = ({
  value = "",
  onChange,
  placeholder,
  title,
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState<PopupPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(containerRef, () => setIsCalendarOpen(false));

  const updatePopupPosition = useCallback(() => {
    const anchor = containerRef.current;
    const popup = popupRef.current;
    if (!anchor || !popup) return;

    const anchorRect = anchor.getBoundingClientRect();
    const popupRect = popup.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const maxLeft = Math.max(VIEWPORT_MARGIN, viewportWidth - popupRect.width - VIEWPORT_MARGIN);
    const left = Math.min(Math.max(anchorRect.left, VIEWPORT_MARGIN), maxLeft);

    const belowTop = anchorRect.bottom + POPUP_GAP;
    const aboveTop = anchorRect.top - POPUP_GAP - popupRect.height;
    const fitsBelow = belowTop + popupRect.height <= viewportHeight - VIEWPORT_MARGIN;
    const fitsAbove = aboveTop >= VIEWPORT_MARGIN;

    let top = belowTop;
    if (!fitsBelow && fitsAbove) {
      top = aboveTop;
    } else if (!fitsBelow) {
      top = Math.max(VIEWPORT_MARGIN, viewportHeight - popupRect.height - VIEWPORT_MARGIN);
    }

    setPopupPosition({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!isCalendarOpen) {
      setPopupPosition(null);
      return undefined;
    }

    updatePopupPosition();
    window.addEventListener("resize", updatePopupPosition);
    window.addEventListener("scroll", updatePopupPosition, true);

    return () => {
      window.removeEventListener("resize", updatePopupPosition);
      window.removeEventListener("scroll", updatePopupPosition, true);
    };
  }, [isCalendarOpen, updatePopupPosition]);

  const openCalendar = () => {
    setPopupPosition(null);
    setIsCalendarOpen(true);
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start] = dates;
    const formattedDate = start ? format(start, "dd.MM.yyyy") : "";
    onChange(formattedDate);
    setIsCalendarOpen(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const handleInputBlur = () => {
    const parsedDate = parseCalendarInputDate(value);
    if (parsedDate) {
      onChange(format(parsedDate, "dd.MM.yyyy"));
    }
  };

  const selectedDate = parseCalendarInputDate(value);

  return (
    <CalendarInputRoot ref={containerRef}>
      <TitleTooltip text={title || ""}>
        <Input
          value={value}
          placeholder={placeholder}
          onChange={handleInputChange}
          onFocus={openCalendar}
          onClick={openCalendar}
          onBlur={handleInputBlur}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              handleInputBlur();
              setIsCalendarOpen(false);
            }
          }}
        />
      </TitleTooltip>
      {isCalendarOpen && (
        <CalendarPopup
          ref={popupRef}
          data-testid="calendar-input-popup"
          $top={popupPosition?.top ?? 0}
          $left={popupPosition?.left ?? 0}
          $ready={Boolean(popupPosition)}
        >
          <Calendar
            onDateChange={handleDateChange}
            startDate={selectedDate}
            isRange={false}
          />
        </CalendarPopup>
      )}
    </CalendarInputRoot>
  );
};

export default DatePickerInput;
